import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

const DEFAULT_FEATURED_IMAGE = '/featured_image.png';

// Deterministic abstract SVG generator for elegant financial placeholder covers
function generatePlaceholderSvg(title: string): string {
  // Simple deterministic hash
  let hash = 0;
  const str = title || "Financial Report";
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);

  const gradients = [
    { from: "#0f172a", to: "#1e293b", accent: "#38bdf8", type: "Grid" }, // Slate modern
    { from: "#1e1b4b", to: "#312e81", accent: "#818cf8", type: "Concentric" }, // Deep Indigo
    { from: "#064e3b", to: "#022c22", accent: "#34d399", type: "Finance Line" }, // Emerald compliance
    { from: "#1c1917", to: "#292524", accent: "#a8a29e", type: "Analytical" }, // Muted warm grey
    { from: "#172554", to: "#1e3a8a", accent: "#60a5fa", type: "Waves" }, // Financial Blue
  ];

  const theme = gradients[hash % gradients.length];
  const gridLineColor = theme.accent + "15"; // Very faint opacity
  const accentColor = theme.accent;

  let patterns = "";
  if (theme.type === "Grid") {
    patterns = `
      <defs>
        <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M 24 0 L 0 0 0 24" fill="none" stroke="${gridLineColor}" stroke-width="1"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <!-- Minimalist financial growth chart line -->
      <path d="M 0,250 Q 150,220 300,150 T 600,80" fill="none" stroke="${accentColor}" stroke-width="2" stroke-linecap="round" opacity="0.4" />
      <circle cx="300" cy="150" r="4" fill="${accentColor}" />
      <circle cx="600" cy="80" r="5" fill="${accentColor}" />
    `;
  } else if (theme.type === "Concentric") {
    patterns = `
      <circle cx="50%" cy="50%" r="180" fill="none" stroke="${gridLineColor}" stroke-width="1.5" />
      <circle cx="50%" cy="50%" r="120" fill="none" stroke="${gridLineColor}" stroke-width="1.2" />
      <circle cx="50%" cy="50%" r="60" fill="none" stroke="${gridLineColor}" stroke-width="1" />
      <!-- Analytical dial elements -->
      <line x1="50%" y1="0" x2="50%" y2="100%" stroke="${gridLineColor}" stroke-width="1" stroke-dasharray="4" />
      <line x1="0" y1="50%" x2="100%" y2="50%" stroke="${gridLineColor}" stroke-width="1" stroke-dasharray="4" />
      <circle cx="50%" cy="50%" r="8" fill="${accentColor}" opacity="0.6" />
    `;
  } else if (theme.type === "Finance Line") {
    patterns = `
      <path d="M-50,280 L100,200 L250,240 L400,100 L550,140 L700,40" fill="none" stroke="${accentColor}" stroke-width="2.5" stroke-linecap="round" opacity="0.5" />
      <path d="M-50,280 L100,200 L250,240 L400,100 L550,140 L700,40 L700,350 L-50,350 Z" fill="url(#grad)" opacity="0.05" />
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="${accentColor}" />
          <stop offset="100%" stop-color="${theme.from}" stop-opacity="0" />
        </linearGradient>
      </defs>
      <!-- Sleek grid overlay -->
      <line x1="100" y1="0" x2="100" y2="350" stroke="${gridLineColor}" stroke-width="1" />
      <line x1="250" y1="0" x2="250" y2="350" stroke="${gridLineColor}" stroke-width="1" />
      <line x1="400" y1="0" x2="400" y2="350" stroke="${gridLineColor}" stroke-width="1" />
      <line x1="550" y1="0" x2="550" y2="350" stroke="${gridLineColor}" stroke-width="1" />
    `;
  } else if (theme.type === "Waves") {
    patterns = `
      <path d="M0,150 C150,100 250,250 400,180 C550,110 650,230 800,150 L800,350 L0,350 Z" fill="${accentColor}" opacity="0.08" />
      <path d="M0,150 C150,100 250,250 400,180 C550,110 650,230 800,150" fill="none" stroke="${accentColor}" stroke-width="2" opacity="0.5" />
      <path d="M0,180 C120,140 220,290 380,210 C540,130 620,270 800,180" fill="none" stroke="${accentColor}" stroke-dasharray="6,4" stroke-width="1" opacity="0.3" />
    `;
  } else {
    patterns = `
      <line x1="50" y1="80" x2="250" y2="220" stroke="${accentColor}" stroke-width="1" opacity="0.3" />
      <line x1="250" y1="220" x2="450" y2="120" stroke="${accentColor}" stroke-width="1" opacity="0.3" />
      <line x1="450" y1="120" x2="600" y2="280" stroke="${accentColor}" stroke-width="1" opacity="0.3" />
      <circle cx="50" cy="80" r="5" fill="${accentColor}" opacity="0.5" />
      <circle cx="250" cy="220" r="7" fill="${accentColor}" opacity="0.7" />
      <circle cx="450" cy="120" r="6" fill="${accentColor}" opacity="0.6" />
      <circle cx="600" cy="280" r="8" fill="${accentColor}" opacity="0.8" stroke="${theme.from}" stroke-width="2" />
    `;
  }

  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 350" width="100%" height="100%">
      <rect width="100%" height="100%" fill="${theme.from}" />
      <circle cx="300" cy="-50" r="250" fill="${accentColor}" opacity="0.05" filter="blur(40px)" />
      ${patterns}
      <text x="32" y="318" fill="white" opacity="0.25" font-family="system-ui, sans-serif" font-size="10" font-weight="700" letter-spacing="0.2em">FINANCIALMETRICS.REPORT</text>
    </svg>
  `.trim();
}

interface SmartImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  aspectRatio?: string;
  priority?: boolean;
}

export const SmartImage: React.FC<SmartImageProps> = ({ 
  src, 
  alt, 
  className = "", 
  aspectRatio = "aspect-video",
  priority = false 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [shouldLoad, setShouldLoad] = useState(priority);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setShouldLoad(priority);
  }, [priority, src]);

  useEffect(() => {
    if (priority || shouldLoad) return;
    if (!src || src.includes('unsplash.com')) {
      setShouldLoad(true);
      return;
    }

    const target = containerRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: '700px 0px' }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [priority, shouldLoad, src]);

  useEffect(() => {
    if (!src || src.includes('unsplash.com')) {
      setSvgContent(null);
      setHasError(false);
      setIsLoading(false);
      return;
    }

    if (!shouldLoad) {
      setSvgContent(null);
      setHasError(false);
      setIsLoading(true);
      return;
    }

    const finalSrc = (src.startsWith('http') || src.startsWith('data:')) 
      ? src 
      : `https://firebasestorage.googleapis.com/v0/b/${(import.meta as any).env.VITE_FIREBASE_STORAGE_BUCKET || 'gen-lang-client-0363978281.firebasestorage.app'}/o/${encodeURIComponent(src)}?alt=media`;

    // 1. Check if the src is a Base64-encoded SVG data URL
    let isSvgRendered = false;
    if (src.startsWith('data:image/svg+xml;base64,')) {
      try {
        let base64Str = src.substring(src.indexOf(',') + 1).trim();
        // Standardize base64 string: remove any whitespace, url encodings, or illegal characters
        if (base64Str.includes('%')) {
          base64Str = decodeURIComponent(base64Str);
        }
        base64Str = base64Str.replace(/\s/g, ''); // Strip all whitespace/newlines
        base64Str = base64Str.replace(/[^A-Za-z0-9+/=]/g, ''); // Remove none-base64 characters

        // Add padding if missing
        while (base64Str.length % 4 !== 0) {
          base64Str += '=';
        }

        const binaryString = window.atob(base64Str);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const decoded = new TextDecoder('utf-8').decode(bytes);
        
        setSvgContent(decoded);
        setHasError(false);
        setIsLoading(false);
        isSvgRendered = true;
      } catch (e) {
        console.warn("[SmartImage] Failed to decode base64 SVG, falling back to direct img element loading:", e);
      }
    }

    // 1.1. Check if the src is a plaintext/URL-encoded SVG data URL
    if (!isSvgRendered && src.startsWith('data:image/svg+xml') && !src.includes(';base64,')) {
      try {
        let rawSvg = src.substring(src.indexOf(',') + 1).trim();
        rawSvg = decodeURIComponent(rawSvg);
        setSvgContent(rawSvg);
        setHasError(false);
        setIsLoading(false);
        isSvgRendered = true;
      } catch (e) {
        console.warn("[SmartImage] Failed to decode URL-encoded SVG, falling back to direct img element loading:", e);
      }
    }

    if (isSvgRendered) return;

    // 2. Remote SVGs should load through <img>. Fetching them for inline SVG
    // rendering requires Firebase Storage CORS headers and creates noisy failures.
    const isSvgUrl = finalSrc.toLowerCase().split('?')[0].endsWith('.svg') || finalSrc.includes('.svg?');
    if (isSvgUrl) {
      setSvgContent(null);
      setIsLoading(true);
      setHasError(false);

      const img = new Image();
      img.src = finalSrc;
      img.onload = () => {
        setHasError(false);
        setIsLoading(false);
      };
      img.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
      return;
    }

    // Default: Reset SVG state and load standard PNG/JPG image
    setSvgContent(null);
    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    img.src = finalSrc;
    img.onload = () => {
      setHasError(false);
      setIsLoading(false);
    };
    img.onerror = (e) => {
      console.warn("[SmartImage] Failed to load standard image:", finalSrc, e);
      setHasError(true);
      setIsLoading(false);
    };
  }, [src, shouldLoad]);

  const displaySrc = (() => {
    if (!src || hasError || src.includes('unsplash.com')) return DEFAULT_FEATURED_IMAGE;

    return (src.startsWith('http') || src.startsWith('data:'))
      ? src
      : `https://firebasestorage.googleapis.com/v0/b/${(import.meta as any).env.VITE_FIREBASE_STORAGE_BUCKET || 'gen-lang-client-0363978281.firebasestorage.app'}/o/${encodeURIComponent(src)}?alt=media`;
  })();

  return (
    <div ref={containerRef} className={`relative overflow-hidden bg-slate-50 ${aspectRatio} ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50">
          <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
        </div>
      )}

      {svgContent ? (
        <div
          className="h-full w-full [&>svg]:block [&>svg]:h-full [&>svg]:w-full [&>svg]:object-cover"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      ) : shouldLoad || !src || hasError || src.includes('unsplash.com') ? (
        <img
          src={displaySrc}
          alt={alt}
          referrerPolicy="no-referrer"
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          className={`h-full w-full object-cover transition-opacity duration-300 ${isLoading && !hasError && src ? 'opacity-0' : 'opacity-100'}`}
        />
      ) : null}
    </div>
  );
};
